import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import {
  DocumentApprovalStatus,
  DocumentApprovalStatusText,
  getDocumentApprovalStatusStyle,
} from "@/constants/enums/document-approval-status.enum";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import ElipsedText from "@/components/common/elipsis/ElipsedText";
interface AddStaffFormProps {
  onClose: () => void;
  onSubmit: () => void;
  isStaffModalOpen: boolean;
  viewData: TodoData;
}

interface StaffFormValues {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  description: string;
  status: string;
}

interface TodoData {
  id: number;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  status: number;
  status_text: string;
}

const ToDoView: React.FC<AddStaffFormProps> = ({
  onClose,
  onSubmit,
  isStaffModalOpen,
  viewData,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [taskData, setTaskData] = useState<TodoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("todoById");
        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customParams: [viewData.id + "/"],
          }),
        );

        if (response.ok) {
          setTaskData(response.data);
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (viewData?.id) {
      fetchTaskDetails();
    }
  }, [viewData?.id, dispatch]);

  // Use taskData from API response, fallback to viewData if not loaded yet
  const displayData = taskData || viewData;

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be at most 50 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be at most 50 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    contactNumber: Yup.string()
      .required("Contact number is required")
      .matches(/^\d{10}$/, "Contact number must be between 10 digits"),
  });

  const handleSubmit = async (values: StaffFormValues) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("staffAdd");

      const response = await dispatch(
        authenticatedCall({
          payload: {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            phone: values.contactNumber,
          },
          method: "post",
        }),
      );

      if (response.ok) {
        toast.success("Staff member added successfully");
        onSubmit();
      } else {
        if (Array.isArray(response.data.message)) {
          response.data.message.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error(response.data.message || "Failed to add staff member");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to add staff member. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("todoById");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          customParams: [viewData.id + "/update-task-status/"],
          payload: {
            id: viewData.id,
          },
        }),
      );

      if (response.ok) {
        toast.success("Task marked as complete");
        onSubmit();
        onClose();
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error updating task status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      contactNumber: "",
      description: "",
      status: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });
  const { bgColor, textColor, dotColor } = getDocumentApprovalStatusStyle(
    displayData.status as DocumentApprovalStatus,
  );
  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-z-50 tw-transition-all tw-ease-in-out tw-duration-1000 ${
        isStaffModalOpen ? "tw-right-0" : "tw-right-[-265px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-screen tw-w-[350px] tw-flex tw-flex-col">
        {/* Sticky Header */}
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <h2 className="tw-text-base tw-font-semibold">Task Details</h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4">
          <form onSubmit={formik.handleSubmit} className="tw-space-y-4">
            {/* Task Title */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Task title
              </label>
              <p className="tw-text-base font-primary-color font-primary tw-font-medium">
                {displayData.name}
              </p>
            </div>
            {/* Status */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Status
              </label>
              <li
                className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${bgColor} ${textColor}`}
              >
                <span
                  className={`tw-w-2 tw-h-2 tw-rounded-full tw-p-1 ${dotColor}`}
                ></span>
                {displayData.status_text || NO_DATA_AVAILABLE}
              </li>
            </div>
            {/* Start Date */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Start Date
              </label>
              <p className="tw-text-base font-primary-color font-primary tw-font-medium">
                {displayData.start_date}
              </p>
            </div>
            {/* End Date */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                End Date
              </label>
              <p className="tw-text-base font-primary-color font-primary tw-font-medium">
                {displayData.end_date}
              </p>
            </div>
            {/* End Date */}
            <div>
              <label className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2">
                Description
              </label>
              <p className="tw-text-base font-primary-color font-primary tw-font-medium">
                <ElipsedText
                  text={displayData.description}
                  shouldWrap={true}
                  showPreferenceButtons={true}
                  maxLength={200}
                />
              </p>
            </div>
          </form>
        </div>

        {/* Sticky Footer - Only shown for pending tasks */}
        {displayData.status === DocumentApprovalStatus.pending && (
          <div className="tw-px-4 tw-py-4 tw-border-t tw-sticky tw-bottom-0 tw-bg-white">
            <button
              onClick={handleMarkComplete}
              disabled={isLoading}
              className="tw-w-full tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#146C94] focus:tw-ring-offset-2 tw-transition-colors disabled:tw-opacity-50"
            >
              {isLoading ? "Updating..." : "Mark as Complete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToDoView;
